import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getJobList,
  deleteDocument,
  renameDocument,
  removeTagFromDocument,
  getTag,
} from "../../utils/Api";
import { useNavigate, useSearchParams } from "react-router-dom";
import DocumentUpload from "../../modals/DocumentUpload";
import DeleteDocumentModal from "../../modals/DeleteDocumentModal";
import AddTagsToDocumentsModal from "../../modals/AddTagsToDocumentsModal";
import DocumentsSkeleton from "../../components/SkeletonLoading/DocumentsSkeleton";
import { useAuth } from "../../utils/AuthContext";
import { useDocumentContext } from "../../contexts/DocumentContext";
import { useUpload } from "../../contexts/UploadContext";

import {
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Tag as TagIcon,
  Share2,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import Tag from "../../components/Tag";
import SmilesExportButton from "../../components/SmilesExportButton";

export type DocumentItem = Record<string, any>;

const PAGE_SIZE = 20;

const Documents: React.FC = () => {
  const { user } = useAuth();
  const { refreshSidebar } = useDocumentContext();
  const { uploads } = useUpload();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documentList, setDocumentList] = useState<DocumentItem[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(
    null,
  );
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [selectedTag, setSelectedTag] = useState<{
    id: string;
    name: string;
    color: string;
  } | null>(null);

  const [justCompletedIds, setJustCompletedIds] = useState<Set<string>>(
    new Set(),
  );

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevUploadsRef = useRef(uploads);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const remainingCount = totalDocuments - documentList.length;

  const getDocumentUploadStatus = (docId: string): string | null => {
    const upload = uploads.find(
      (u) =>
        u.documentId === docId &&
        u.status !== "complete" &&
        u.status !== "error",
    );
    return upload ? upload.status : null;
  };

  const fetchDocuments = useCallback(
    async (offset: number, append: boolean, limitOverride?: number) => {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);

        const tagId = searchParams.get("tag");
        const params: Parameters<typeof getJobList>[0] = {
          limit: limitOverride ?? PAGE_SIZE,
          offset,
          sort_order: sortOrder,
        };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        if (tagId && tagId !== "uncategorized") params.tag_id = tagId;
        if (tagId === "uncategorized") params.untagged = true;

        const { data } = await getJobList(params);

        let docs: DocumentItem[] = [];
        if (data.documents && typeof data.documents === "object") {
          docs = Object.entries(data.documents).map(
            ([id, docData]: [string, any]) => ({
              ...docData,
              id,
            }),
          );
        }

        // Filter to personal & shared docs (exclude org docs user doesn't own)
        const personalAndSharedDocs = docs.filter(
          (doc) =>
            !doc.organisation_id ||
            doc.user_id === user?.uid ||
            doc.is_shared_with_me,
        );

        if (append) {
          setDocumentList((prev) => [...prev, ...personalAndSharedDocs]);
        } else {
          setDocumentList(personalAndSharedDocs);
        }
        setTotalDocuments(data.total_documents ?? 0);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Error fetching documents");
        if (!append) setDocumentList([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, sortOrder, searchParams, user?.uid],
  );

  useEffect(() => {
    fetchDocuments(0, false);
  }, [fetchDocuments]);

  useEffect(() => {
    const prev = prevUploadsRef.current;
    const newlyCompleted = uploads.filter(
      (u) =>
        u.status === "complete" &&
        u.documentId &&
        prev.find((p) => p.id === u.id)?.status !== "complete",
    );
    if (newlyCompleted.length > 0) {
      fetchDocuments(0, false);
      setJustCompletedIds((prev) => {
        const next = new Set(prev);
        newlyCompleted.forEach((u) => next.add(u.documentId!));
        return next;
      });
    }
    prevUploadsRef.current = uploads;
  }, [uploads]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleShowMore = useCallback(() => {
    fetchDocuments(documentList.length, true);
  }, [fetchDocuments, documentList.length]);

  useEffect(() => {
    if (remainingCount <= 0 || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleShowMore();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    );

    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [remainingCount, loadingMore, loading, handleShowMore]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  useEffect(() => {
    let cancelled = false;

    const tagId = searchParams.get("tag");
    if (tagId) {
      if (tagId === "uncategorized") {
        setSelectedTag({
          id: "uncategorized",
          name: "Uncategorised",
          color: "#9CA3AF",
        });
      } else {
        getTag(tagId)
          .then((response) => {
            if (!cancelled) {
              setSelectedTag(response.data);
            }
          })
          .catch((error) => {
            if (!cancelled) {
              console.error("Failed to load tag:", error);
              setSelectedTag(null);
            }
          });
      }
    } else {
      setSelectedTag(null);
    }

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const refetch = () => {
    const currentCount = Math.max(documentList.length, PAGE_SIZE);
    fetchDocuments(0, false, currentCount);
  };

  const handleEditClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(doc.id);
    setEditingName(doc.name);
  };

  const handleSaveEdit = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await renameDocument(docId, editingName);
      setEditingDocId(null);
      setEditingName("");
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error renaming document");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(null);
    setEditingName("");
  };

  const handleDeleteClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        await Promise.all(
          Array.from(selectedDocIds).map((id) => deleteDocument(id)),
        );
        setSelectedDocIds(new Set());
      } else if (documentToDelete) {
        await deleteDocument(documentToDelete.id);
        setDocumentToDelete(null);
      }
      setIsDeleteModalOpen(false);
      setIsBulkDelete(false);
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error deleting document(s)");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(
        documentList
          .filter((doc) => !doc.is_shared_with_me)
          .map((doc) => doc.id),
      );
      setSelectedDocIds(allIds);
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleSelectDocument = (docId: string, checked: boolean) => {
    const newSelected = new Set(selectedDocIds);
    if (checked) {
      newSelected.add(docId);
    } else {
      newSelected.delete(docId);
    }
    setSelectedDocIds(newSelected);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedDocIds.size === 0) return;
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleRemoveTag = async (tagId: string, documentId: string) => {
    try {
      await removeTagFromDocument(tagId, documentId);
      refetch();
      refreshSidebar();
    } catch (err: any) {
      setError(err.message ?? "Error removing tag from document");
    }
  };

  const selectableDocuments = documentList.filter(
    (doc) => !doc.is_shared_with_me,
  );
  const allDisplayedSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((doc) => selectedDocIds.has(doc.id));

  // Build placeholder rows for uploads that are active and not yet in the fetched list
  const activeUploadPlaceholders: DocumentItem[] = uploads
    .filter(
      (u) =>
        u.documentId &&
        u.status !== "complete" &&
        u.status !== "error" &&
        !documentList.some((doc) => doc.id === u.documentId),
    )
    .map((u) => ({
      id: u.documentId as string,
      name: u.documentName,
      page_count: 0,
      tags: [],
      created_at: null,
      _isUploadPlaceholder: true,
    }));

  const displayedDocuments = [...activeUploadPlaceholders, ...documentList];

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              {selectedTag ? selectedTag.name : "All documents"}
            </h3>
            <p className="text-base text-gray-500 mt-0.5">
              {selectedTag
                ? `Showing documents labeled with "${selectedTag.name}"`
                : "View, edit and analyse your documents"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {selectedDocIds.size > 0 && (
              <div className="flex items-center gap-1 border border-gray-200 rounded-lg bg-white h-9 w-fit px-1">
                <button
                  onClick={() => setIsAddTagsModalOpen(true)}
                  disabled={isDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <TagIcon size={14} />
                  Tag
                </button>
                <div className="w-px h-4 bg-gray-200" />
                <button
                  onClick={handleDeleteSelectedClick}
                  disabled={isDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            )}

            <SmilesExportButton mode="combined" disabled={totalDocuments === 0} />
          </div>
        </div>

        <div className="mb-5 flex gap-3 items-center">
          <input
            type="text"
            className="flex-1 max-w-sm h-10 px-3 text-sm border border-gray-200 rounded-lg bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
            placeholder="Search documents..."
            aria-label="Search documents"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10">
        {loading ? (
          <DocumentsSkeleton />
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="px-3 sm:px-5 py-2.5 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allDisplayedSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                      aria-label="Select all documents"
                      disabled={documentList.length === 0}
                    />
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                    Name
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400 hidden md:table-cell">
                    Labels
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400 hidden lg:table-cell">
                    Owner
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400 hidden sm:table-cell">
                    Pages
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                    <button
                      onClick={toggleSortOrder}
                      className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      title={`Sort by date ${sortOrder === "asc" ? "descending" : "ascending"}`}
                      disabled={documentList.length === 0}
                    >
                      Created
                      {sortOrder === "desc" ? (
                        <ArrowDown size={12} />
                      ) : (
                        <ArrowUp size={12} />
                      )}
                    </button>
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {documentList.length === 0 &&
                activeUploadPlaceholders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="text-gray-400 text-sm">
                        {searchQuery ? (
                          <>
                            <p className="font-medium text-gray-600 mb-1">No documents found</p>
                            <p>No documents match &ldquo;{searchQuery}&rdquo;</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-gray-600 mb-1">No documents</p>
                            <p>Create your first document to get started</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedDocuments.map((doc) => {
                    const uploadStatus = getDocumentUploadStatus(doc.id);
                    const uploading = uploadStatus !== null;
                    return (
                      <tr
                        key={doc.id}
                        className={`group transition-colors duration-100 ${
                          uploading ? "opacity-60" : "hover:bg-gray-50/50"
                        }`}
                      >
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap">
                          {!doc.is_shared_with_me && (
                            <input
                              type="checkbox"
                              checked={selectedDocIds.has(doc.id)}
                              onChange={(e) =>
                                handleSelectDocument(doc.id, e.target.checked)
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d] disabled:cursor-not-allowed"
                              aria-label={`Select ${doc.name || "document"}`}
                              disabled={uploading}
                            />
                          )}
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap">
                          {editingDocId === doc.id ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d]"
                              autoFocus
                            />
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {doc.organisation_name && (
                                <span className="text-xs text-gray-400">
                                  {doc.organisation_name}
                                </span>
                              )}
                              <div className="flex items-center gap-2">
                                {uploading ? (
                                  <span className="text-sm text-gray-400 flex items-center gap-2">
                                    {doc.name || "Untitled Document"}
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded">
                                      <Loader2 size={10} className="animate-spin" />
                                      {uploadStatus === "processing" ? "Processing" : "Uploading"}
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    className="font-medium text-[#1d365d] hover:text-[#2d4a75] cursor-pointer flex items-center gap-2"
                                    onClick={() => {
                                      setJustCompletedIds((prev) => {
                                        const next = new Set(prev);
                                        next.delete(doc.id);
                                        return next;
                                      });
                                      navigate(`/view/${doc.id}`);
                                    }}
                                  >
                                    {doc.name || "Untitled Document"}
                                    {justCompletedIds.has(doc.id) && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded">
                                        <CheckCircle2 size={10} />
                                        Ready
                                      </span>
                                    )}
                                  </span>
                                )}
                                {doc.is_shared_with_me && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded"
                                    title="Shared with you"
                                  >
                                    <Share2 size={10} />
                                    Shared
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-5 py-3.5 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {doc.tags && doc.tags.length > 0 ? (
                              doc.tags.map((tag: any) => (
                                <Tag
                                  key={tag.id}
                                  id={tag.id}
                                  name={tag.name}
                                  color={tag.color}
                                  onRemove={(tagId) =>
                                    handleRemoveTag(tagId, doc.id)
                                  }
                                />
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm text-gray-600 hidden lg:table-cell">
                          {doc.owner_name || "—"}
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm text-gray-600 hidden sm:table-cell">
                          {doc.page_count || 0}
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm text-gray-500">
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                            : "Just now"}
                        </td>
                        <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm">
                          {editingDocId === doc.id ? (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={(e) => handleSaveEdit(doc.id, e)}
                                className="text-xs font-medium text-[#1d365d] hover:text-[#2d4a75]"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-xs font-medium text-gray-400 hover:text-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                              {!doc.is_shared_with_me && !uploading && (
                                <>
                                  <button
                                    onClick={(e) => handleEditClick(doc, e)}
                                    className="text-gray-400 hover:text-[#1d365d] transition-colors"
                                    title="Rename"
                                  >
                                    <Pencil size={15} />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteClick(doc, e)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* pagination controls */}
        {!loading && documentList.length > 0 && (
          <div className="mt-6 pb-12 flex flex-col items-center gap-3">
            <div ref={sentinelRef} />
            {loadingMore && (
              <p className="text-sm text-gray-400">Loading...</p>
            )}
            <p className="text-sm text-gray-400">
              {documentList.length} of {totalDocuments} documents
            </p>
          </div>
        )}
      </div>

      <DocumentUpload
        mode="create"
        isOpen={isUploadModalOpen}
        setIsOpen={setIsUploadModalOpen}
      />

      <DeleteDocumentModal
        document={documentToDelete}
        isDeleting={isDeleting}
        onConfirmDelete={confirmDelete}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setIsBulkDelete(false);
        }}
        bulkDeleteCount={isBulkDelete ? selectedDocIds.size : undefined}
      />

      <AddTagsToDocumentsModal
        isOpen={isAddTagsModalOpen}
        setIsOpen={setIsAddTagsModalOpen}
        documentIds={Array.from(selectedDocIds)}
        onComplete={() => {
          setSelectedDocIds(new Set());
          refetch();
          refreshSidebar();
        }}
      />
    </div>
  );
};

export default Documents;
